/**
 * Login Activity Logs — Company Admin read-only directory of every
 *  user in the tenant with their last login / logout / activity
 *  stamps + a derived Online/Offline status.
 *
 *  Backed by `GET /admin/login-activity` (admin.routes.js). The
 *  endpoint is gated on `audit_log.view` so a Sub Admin without
 *  that grant won't see the sidebar entry OR be able to hit the
 *  endpoint directly.
 *
 *  Online/Offline derivation lives server-side (5-min window from
 *  `lastActiveAt`); the frontend just renders what the API returns
 *  so the threshold can be tuned in one place.
 *
 *  The page auto-refreshes every 30s via React Query's
 *  `refetchInterval` — short enough that the Online pill stays
 *  meaningful, long enough that the request volume is bounded.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ShieldCheck, AlertTriangle, Users, Activity, CircleSlash } from 'lucide-react'
import { api } from '../../lib/api'
import { formatDateTime, formatNumber } from '../../lib/format'
import { loginActivityResponse, loginFailuresResponse } from '../../lib/types'
import { Modal } from '../../components/ui/Modal'
import { useAuthStore } from '../../stores/authStore'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FilterRow } from '../../components/filters/FilterRow'
import { RolePill } from '../../components/ui/Pills'
import { Avatar } from '../../components/Avatar'
import { DataTable } from '../../components/table/DataTable'

/** Map the (role, workRole) tuple to a single human-readable label
 *  used for the role badge column. */
function roleLabel(row) {
  if (row.role === 'company_admin') return 'Company Admin'
  if (row.role === 'sub_admin') return 'Sub Admin'
  if (row.role === 'super_admin') return 'Super Admin'
  // Non-admin users show their ASSIGNED Work Role name (e.g. "Team
  // Leader"); fall back to the tier label only when no custom Work Role
  // is set.
  if (row.workRoleName) return row.workRoleName
  if (row.workRole === 'manager') return 'Manager'
  if (row.workRole === 'team_leader') return 'Team Leader'
  return 'Employee'
}

/** Role label → shared RolePill tone. Same colour families the badge
 *  carried before, now rendered with the All Users pill chrome. */
const ROLE_TONE = {
  'Company Admin': 'amber',
  'Sub Admin': 'accent',
  'Super Admin': 'danger',
  Manager: 'info',
  'Team Leader': 'success',
  Employee: 'muted',
}

/** Absolute timestamp in the app-wide DD/MM/YYYY HH:MM AM/PM format
 *  (`lib/format.formatDateTime`, the same helper the Audit Log and the
 *  rest of the panel use). Falls back to "—" for null / invalid inputs
 *  so empty cells never crash render. */
function fmtAbs(iso) {
  if (!iso) return '—'
  return formatDateTime(iso) || '—'
}

/** Relative timestamp used in the Last Activity column — friendlier
 *  for the "online now" + "5 min ago" cases. Falls through to absolute
 *  for anything older than a week so the user still has a date anchor. */
function fmtRel(iso) {
  if (!iso) return 'never'
  try {
    const ms = Date.now() - new Date(iso).valueOf()
    if (Number.isNaN(ms) || ms < 0) return '—'
    const sec = Math.floor(ms / 1000)
    if (sec < 10) return 'just now'
    if (sec < 60) return `${sec} sec ago`
    const min = Math.floor(sec / 60)
    if (min < 60) return `${min} min ago`
    const hr = Math.floor(min / 60)
    if (hr < 24) return `${hr} hr ago`
    const day = Math.floor(hr / 24)
    if (day === 1) return 'yesterday'
    if (day < 7) return `${day} days ago`
    return fmtAbs(iso)
  } catch {
    return '—'
  }
}

/** Logout Time display for a row. `lastLogoutAt` is the most recent
 *  *explicit* sign-out and is tracked independently of `lastLoginAt` (the
 *  most recent sign-in) — only a "Sign out" click stamps it, never a
 *  closed tab or an expired session. So when the last sign-in is more
 *  recent than the last sign-out (the user signed in again and hasn't
 *  signed out since — still signed in, or the session simply lapsed) the
 *  stored logout belongs to an *earlier* session; showing it against this
 *  row's login reads as "logged out before logging in". Suppress it to
 *  "—" so the column only ever shows a sign-out that falls on/after the
 *  shown sign-in, and explain the dash with a tooltip. Returns
 *  `{ text, title }`. */
function logoutDisplay(row) {
  const loginMs = row.lastLoginAt ? new Date(row.lastLoginAt).valueOf() : null
  const logoutMs = row.lastLogoutAt ? new Date(row.lastLogoutAt).valueOf() : null
  // Show the sign-out only when it falls on/after the shown sign-in.
  if (logoutMs !== null && loginMs !== null && logoutMs >= loginMs) {
    return { text: fmtAbs(row.lastLogoutAt), title: undefined }
  }
  // A current sign-in with no matching later sign-out → explain the dash.
  // (No tooltip when the user has never signed in — the Login Time dash
  // already says as much.)
  return {
    text: '—',
    title: loginMs !== null ? 'No sign-out since last sign-in' : undefined,
  }
}

export function LoginActivityPage() {
  const [statusFilter, setStatusFilter] = useState('') // '' | 'online' | 'offline'
  // Separate from presence — see the API route for why they are orthogonal.
  // Keeping them apart makes "Online + with failed sign-ins" expressible.
  const [failedOnly, setFailedOnly] = useState(false)
  const [roleFilter, setRoleFilter] = useState('')
  const [search, setSearch] = useState('')
  // Drill-down: the user whose individual failed sign-in records are open.
  const [drillUser, setDrillUser] = useState(null)
  // Auth gate — prevents the 30-second auto-refetch from firing
  // during sign-out transitions (the page can remain briefly mounted
  // while the cookie is being cleared, which would otherwise cause
  // a 401 in the network log).
  const authStatus = useAuthStore((s) => s.status)
  const isAuthed = authStatus === 'authenticated'

  // Live polling — 30s. React Query handles the interval + revalidation
  // on focus so the page stays fresh without a manual refresh.
  const query = useQuery({
    queryKey: ['admin', 'login-activity', { statusFilter, failedOnly, search }],
    queryFn: async () => {
      const res = await api.get('/admin/login-activity', {
        params: {
          status: statusFilter || undefined,
          failed: failedOnly ? 'true' : undefined,
          // Role is filtered client-side (below) against the resolved Work
          // Role label, so it isn't sent to the server.
          search: search.trim() || undefined,
        },
      })
      return loginActivityResponse.parse(res.data)
    },
    enabled: isAuthed,
    refetchInterval: isAuthed ? 30_000 : false,
    refetchOnWindowFocus: isAuthed,
  })

  const rawRows = useMemo(() => query.data?.data ?? [], [query.data])

  // Role filter is client-side against the displayed label (admins show
  // "Company Admin"/"Sub Admin"; everyone else shows their Work Role), so
  // the dropdown lists exactly the roles actually present in the data.
  const roleOptions = useMemo(() => {
    const labels = [...new Set(rawRows.map(roleLabel))].sort((a, b) =>
      a.localeCompare(b),
    )
    return [
      { value: '', label: 'All roles' },
      ...labels.map((l) => ({ value: l, label: l })),
    ]
  }, [rawRows])
  const roleFilteredRows = useMemo(
    () =>
      roleFilter ? rawRows.filter((r) => roleLabel(r) === roleFilter) : rawRows,
    [rawRows, roleFilter],
  )

  // KPI tiles reflect the CURRENTLY-FILTERED result set (search + status +
  // role) so the numbers never contradict the table below. The global
  // tenant total is still shown in the "Showing X of N" footer for context.
  const kpi = useMemo(() => {
    let online = 0
    let offline = 0
    let failed = 0
    // Total failed sign-in ATTEMPTS (from the permanent log, per-user counts)
    // across the visible users — the "Failed Attempts" card. Distinct from
    // `failed`, which counts affected users.
    let failedAttempts = 0
    for (const r of roleFilteredRows) {
      if (r.status === 'online') online += 1
      else offline += 1
      if (r.recentlyFailed) failed += 1
      failedAttempts += r.failedLoginAttempts || 0
    }
    return {
      all: roleFilteredRows.length,
      online,
      offline,
      failed,
      failedAttempts,
    }
  }, [roleFilteredRows])

  // The role-filtered set feeds BOTH the KPI tiles and the DataTable,
  // which owns sorting / pagination / columns / export from here.
  const rows = roleFilteredRows

  const columns = useMemo(
    () => [
      {
        key: 'fullName',
        header: 'User Name',
        grow: true,
        pinnable: true,
        minWidth: 220,
        sortAccessor: (r) => (r.fullName ?? '').toLowerCase(),
        searchAccessor: (r) =>
          `${r.fullName ?? ''} ${r.emailId ?? ''} ${r.lastSignInIp ?? ''}`,
        exportValue: (r) => r.fullName,
        render: (r) => <NameCell r={r} onDrill={setDrillUser} />,
      },
      {
        key: 'emailId',
        header: 'Email ID',
        minWidth: 220,
        sortAccessor: (r) => (r.emailId ?? '').toLowerCase(),
        exportValue: (r) => r.emailId,
        render: (r) => (
          <span className="text-[color:var(--color-ink)]">{r.emailId}</span>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        minWidth: 150,
        cellClassName: 'whitespace-nowrap',
        sortAccessor: (r) => roleLabel(r),
        exportValue: (r) => roleLabel(r),
        render: (r) => {
          const l = roleLabel(r)
          return <RolePill label={l} tone={ROLE_TONE[l] ?? 'muted'} />
        },
      },
      {
        key: 'lastLoginAt',
        header: 'Login Time',
        minWidth: 170,
        sortAccessor: (r) => (r.lastLoginAt ? new Date(r.lastLoginAt) : null),
        exportValue: (r) => fmtAbs(r.lastLoginAt),
        render: (r) => (
          <span className="text-[color:var(--color-ink)] tabular-nums whitespace-nowrap">
            {fmtAbs(r.lastLoginAt)}
          </span>
        ),
      },
      {
        key: 'lastLogoutAt',
        header: 'Logout Time',
        minWidth: 170,
        sortAccessor: (r) => (r.lastLogoutAt ? new Date(r.lastLogoutAt) : null),
        exportValue: (r) => logoutDisplay(r).text,
        render: (r) => {
          const lo = logoutDisplay(r)
          return (
            <span
              title={lo.title}
              className="text-[color:var(--color-ink)] tabular-nums whitespace-nowrap"
            >
              {lo.text}
            </span>
          )
        },
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 110,
        sortAccessor: (r) => (r.status === 'online' ? 0 : 1),
        exportValue: (r) => r.status,
        render: (r) => <StatusPill online={r.status === 'online'} />,
      },
      {
        key: 'lastActiveAt',
        header: 'Last Activity',
        minWidth: 140,
        sortAccessor: (r) => (r.lastActiveAt ? new Date(r.lastActiveAt) : null),
        exportValue: (r) => fmtAbs(r.lastActiveAt),
        render: (r) => (
          <span className="text-[color:var(--color-ink-muted)] whitespace-nowrap">
            {fmtRel(r.lastActiveAt)}
          </span>
        ),
      },
      {
        key: 'ip',
        header: 'IP Address',
        minWidth: 130,
        sortAccessor: (r) => r.lastSignInIp ?? '',
        exportValue: (r) => r.lastSignInIp ?? '',
        render: (r) => (
          <span className="text-[12px] text-[color:var(--color-ink-muted)] font-mono whitespace-nowrap">
            {r.lastSignInIp || '—'}
          </span>
        ),
      },
    ],
    [],
  )

  function clearFilters() {
    setStatusFilter('')
    setFailedOnly(false)
    setRoleFilter('')
    setSearch('')
  }

  const hasAnyFilter =
    statusFilter !== '' || failedOnly || roleFilter !== '' || search.trim() !== ''

  return (
    <div>
      <Breadcrumb current="Login Activity Logs" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Activity size={22} className="text-[color:var(--color-accent-strong)]" />
          Login Activity Logs
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Track who&apos;s signed in, when they last logged in, and current activity status.
        </p>
      </div>

      {/* KPI strip — read-only stat tiles using the platform KpiCard
       *  shape (rounded-xl chrome, halo bleed top-right, tinted icon
       *  chip) so this page reads in the same KPI vocabulary as the
       *  rest of the admin surface. Figures track the CURRENTLY-FILTERED
       *  result set (search + status + role) so they never contradict the
       *  table below; the global tenant total stays in the footer. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard
          label="Users"
          value={kpi.all}
          icon={Users}
          tone="violet"
          loading={query.isLoading}
        />
        <KpiCard
          label="Online Now"
          value={kpi.online}
          icon={Activity}
          tone="emerald"
          loading={query.isLoading}
        />
        <KpiCard
          label="Offline"
          value={kpi.offline}
          icon={CircleSlash}
          tone="slate"
          loading={query.isLoading}
        />
        <KpiCard
          label="Failed Attempts"
          value={kpi.failedAttempts}
          icon={AlertTriangle}
          tone="rose"
          loading={query.isLoading}
        />
      </div>

      {/* Toolbar — shared FilterRow primitive. Search, a Status select
       *  (replacing the old clickable count chips), a Role select, and
       *  a Clear that appears only when a filter is active. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search Name, Email ID, IP"
        />
        <FilterRow.Select
          ariaLabel="Status"
          placeholder="All statuses"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'online', label: 'Online' },
            { value: 'offline', label: 'Offline' },
          ]}
        />
        {/* Separate from Status on purpose — a failed sign-in is a rolling
          *  24h security tally, not a presence state. As its own filter it
          *  composes with Status and Role. */}
        <FilterRow.Select
          ariaLabel="Sign-in failures"
          placeholder="All sign-ins"
          value={failedOnly ? 'failed' : ''}
          onChange={(v) => setFailedOnly(v === 'failed')}
          options={[
            { value: '', label: 'All sign-ins' },
            { value: 'failed', label: 'With failed sign-in (24h)' },
          ]}
        />
        <FilterRow.Select
          ariaLabel="Role"
          placeholder="All roles"
          value={roleFilter}
          onChange={setRoleFilter}
          options={roleOptions}
        />
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
      </FilterRow>

      {/* Clarify that "Failed sign-in" is a security filter, not a presence
       *  status — the Status column keeps showing Online/Offline for these
       *  rows, which reads as a contradiction without this note. */}
      {failedOnly ? (
        <div className="mb-4 rounded-lg border border-[color:var(--color-info-border)] bg-[color:var(--color-info-bg)] px-4 py-2.5 text-sm text-[color:var(--color-ink-strong)]">
          Showing users with a failed sign-in in the last 24 hours. The Status
          column still reflects their current online/offline presence.
        </div>
      ) : null}

      <DataTable
        tableId="app-login-activity"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={query.isLoading}
        error={query.isError ? 'Could not load login activity.' : null}
        onRetry={() => query.refetch()}
        title="Login Activity"
        exportBaseName="login-activity"
        exportTitle="Login Activity"
        searchable={false}
        selectable
        defaultSort={[{ key: 'lastActiveAt', dir: 'desc' }]}
        emptyState={{
          icon: Users,
          title: hasAnyFilter ? 'No users match these filters' : 'No users yet',
          body: hasAnyFilter
            ? 'Try a different search term, status or role, or clear the filters.'
            : 'Users in this company will appear here once they sign in.',
        }}
      />

      <p className="mt-4 text-center text-xs text-[color:var(--color-ink-subtle)]">
        Auto-refreshes every 30 seconds.
      </p>

      {/* Inline keyframes for the pulse animation on the Online dot.
       *  Scoped to this page via the dot's inline `animation` style. */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
      `}</style>

      <FailuresModal user={drillUser} onClose={() => setDrillUser(null)} />
    </div>
  )
}

/** Friendly labels for the persisted failure reasons (models/LoginAttempt.js). */
const REASON_LABEL = {
  invalid_password: 'Invalid password',
  account_locked: 'Account locked',
  inactive_account: 'Inactive account',
  no_membership: 'No membership',
  unknown_email: 'Unknown email',
  organisation_unavailable: 'Company unavailable',
}

/** Drill-down modal: the individual failed sign-in records for one user —
 *  time, reason, IP, and user agent. Fetched on open from the permanent log. */
function FailuresModal({ user, onClose }) {
  const q = useQuery({
    queryKey: ['login-failures', user?.id],
    queryFn: async () => {
      const res = await api.get(`/admin/login-activity/${user.id}/failures`)
      return loginFailuresResponse.parse(res.data)
    },
    enabled: Boolean(user?.id),
  })
  const records = q.data?.data ?? []
  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      title={`Failed sign-ins${user?.fullName ? ` — ${user.fullName}` : ''}`}
      description="Individual failed sign-in records for this user, newest first."
      size="lg"
    >
      {q.isLoading ? (
        <p className="py-6 text-center text-sm text-[color:var(--color-ink-muted)]">
          Loading…
        </p>
      ) : q.isError ? (
        <p className="py-6 text-center text-sm text-[color:var(--color-priority-urgent)]">
          Couldn&apos;t load failed sign-in records.
        </p>
      ) : records.length === 0 ? (
        <p className="py-6 text-center text-sm text-[color:var(--color-ink-muted)]">
          No failed sign-in records for this user.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)]">
                <th className="py-2 pr-3 font-medium whitespace-nowrap">When</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap">Reason</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap">IP address</th>
                <th className="py-2 font-medium">User agent</th>
              </tr>
            </thead>
            <tbody>
              {records.map((rec) => (
                <tr
                  key={rec.id}
                  className="border-b border-[color:var(--color-rule)] last:border-b-0 align-top"
                >
                  <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-[color:var(--color-ink)]">
                    {fmtAbs(rec.at)}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-white/[0.06] dark:text-red-300 dark:border-white/10">
                      {REASON_LABEL[rec.reason] ?? rec.reason ?? 'Failed'}
                    </span>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-[color:var(--color-ink-muted)]">
                    {rec.ipAddress ?? '—'}
                  </td>
                  <td className="py-2 text-xs text-[color:var(--color-ink-muted)] break-all max-w-[420px]">
                    {rec.userAgent ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  )
}

/** User Name cell — avatar + name, plus a clickable "failed sign-in"
 *  badge that opens the per-user drill-down modal. */
function NameCell({ r, onDrill }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <Avatar name={r.fullName} avatarUrl={r.avatarUrl} size="sm" />
      <span className="font-medium text-[color:var(--color-ink-strong)] truncate">
        {r.fullName}
      </span>
      {r.failedLoginAttempts > 0 || r.recentlyFailed ? (
        <button
          type="button"
          onClick={() => onDrill({ id: r.id, fullName: r.fullName })}
          className={
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 cursor-pointer transition-colors ' +
            (r.recentlyFailed
              ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 dark:bg-white/[0.06] dark:text-red-300 dark:border-white/10'
              : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10')
          }
          title={
            r.failedLoginAttempts > 0
              ? `${r.failedLoginAttempts} failed sign-in attempt${
                  r.failedLoginAttempts === 1 ? '' : 's'
                }${
                  r.lastFailedLoginAt
                    ? ' — last at ' + fmtAbs(r.lastFailedLoginAt)
                    : ''
                }. Click to view records.`
              : `Recent failed sign-in — last at ${fmtAbs(r.lastFailedLoginAt)}. Click to view records.`
          }
        >
          <AlertTriangle size={9} />
          {r.failedLoginAttempts > 0
            ? `${r.failedLoginAttempts} failed`
            : 'Recent fail'}
        </button>
      ) : null}
    </div>
  )
}

/** Online / Offline presence pill. The Online dot pulses via the
 *  page-scoped `pulse` keyframe (see the inline <style> in the page). */
function StatusPill({ online }) {
  if (online) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-200 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10">
        <span
          className="w-1.5 h-1.5 rounded-full bg-emerald-500"
          style={{
            boxShadow: '0 0 0 2px rgba(34,197,94,0.25)',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
        Online
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold dark:bg-white/[0.06] dark:text-slate-300">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      Offline
    </span>
  )
}

// Tone palette mirrors the Companies / All Users KpiCard tokens so the
// admin KPI strips share one colour vocabulary. `slate` is the muted
// tile used for the neutral "Offline" count.
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
  slate: {
    tile: 'bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10',
    halo: 'from-[#F1F5F9] to-transparent dark:from-transparent',
  },
}

/** Single-stat KPI card matching the Companies / All Users shape —
 *  rounded-xl chrome, blurred halo bleed top-right, 12×12 tinted icon
 *  chip, font-display value, uppercase tracking-wider label. Skeleton
 *  state during the initial fetch swaps the number for a pulsing
 *  placeholder so the strip doesn't pop in. */
function KpiCard({ label, value, icon: Icon, tone = 'violet', loading }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px] transition-all duration-200 hover:border-[color:var(--color-accent-border)] hover:shadow-lg hover:-translate-y-0.5">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          {loading ? (
            <div className="mt-3 h-7 w-14 rounded animate-pulse bg-[color:var(--color-rule)]" />
          ) : (
            <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
              {formatNumber(Number(value ?? 0))}
            </p>
          )}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}
